<template>
  <v-list dense class="main-menu">
    <v-list-item class="avatar mt-6 mb-2">
      <v-list-item-icon>
        <v-icon>mdi-account-outline</v-icon>
      </v-list-item-icon>
      <v-list-item-title>
        <div class="title-icon">
          <span>
            <h6 class="title mb-0">{{ currentUser.firstName + ' ' + currentUser.lastName }}</h6>
          </span>
          <v-menu offset-y>
            <template v-slot:activator="{on}">
              <v-icon v-on="on">$sl-menu</v-icon>
            </template>
            <v-list dense>
              <v-list-item @click="$nextTick(() => $router.push({name: 'User', params: {id: currentUser.id}}))">
                <v-list-item-icon> <v-icon>$sl-user</v-icon> </v-list-item-icon>Min profil
              </v-list-item>
            </v-list>
          </v-menu>
        </div>
      </v-list-item-title>
    </v-list-item>
    <v-list-item to="/" link>
      <v-list-item-icon>
        <v-icon>$sl-monitor-chart</v-icon>
      </v-list-item-icon>
      <v-list-item-title class="v-step-1">Översikt</v-list-item-title>
    </v-list-item>

    <template v-for="(item, index) in items">
      <template v-if="item.children">
        <v-list-group no-action sub-group :key="index">
          <template v-slot:activator>
            <v-list-item-content>
              <v-list-item-title>{{ item.title }}</v-list-item-title>
            </v-list-item-content>
          </template>

          <v-list-item v-for="(child, i) in item.children" :key="i" link :to="child.to">
            <v-icon v-text="child.icon"></v-icon>

            <v-list-item-title v-text="child.title"></v-list-item-title>
          </v-list-item>
        </v-list-group>
      </template>

      <template v-else>
        <v-list-item
          :data-cy="`menu-${item.title}`"
          exact
          :key="index"
          :to="item.to"
          v-if="
            typeof item.enforce === 'undefined' ||
            (item.enforce &&
              currentPermissions[item.enforce[0]] &&
              currentPermissions[item.enforce[0]][item.enforce[1]])
          "
        >
          <v-list-item-icon>
            <v-icon>{{ item.icon }}</v-icon>
          </v-list-item-icon>
          <v-list-item-title :class="item.class">{{ item.title }}</v-list-item-title>
        </v-list-item>
      </template>
    </template>

    <template v-if="currentPermissions.admin && currentPermissions.admin.list">
      <v-list-group no-action>
        <template v-slot:activator>
          <v-list-item-icon>
            <v-icon>$sl-shield-settings</v-icon>
          </v-list-item-icon>
          <v-list-item-content>
            <v-list-item-title>Administration</v-list-item-title>
          </v-list-item-content>
        </template>

        <v-list-item v-for="(admin, i) in adminItems" :key="i" link :to="admin.to" :tag="admin.tag" class="child" v-if="!admin.canDisplay || admin.canDisplay(currentUser)" @click="admin.onClick && handleAdminClick(admin)">
            <v-list-item-icon>
              <v-icon v-text="admin.icon"></v-icon>
            </v-list-item-icon>
            <v-list-item-title v-text="admin.title"></v-list-item-title>
        </v-list-item>
      </v-list-group>
    </template>

    <v-divider class="my-2"></v-divider>
    <v-dialog v-model="scbDialog" max-width="400px" persistent>
      <v-card>
        <v-card-title>SCB Statistik</v-card-title>
        <v-card-text>
          <v-text-field
            v-model="scbYear"
            label="År"
            type="number"
            outlined
            dense
            @keyup.enter="submitScbStatistics"
          />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="scbDialog = false">Avbryt</v-btn>
          <v-btn color="primary" @click="submitScbStatistics" :disabled="String(scbYear).length !== 4">OK</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-list-group no-action>
      <template v-slot:activator>
        <v-list-item-icon>
          <v-icon>$sl-question-circle</v-icon>
        </v-list-item-icon>
        <v-list-item-content>
          <v-list-item-title>Hjälp</v-list-item-title>
        </v-list-item-content>
      </template>

      <v-list-item
        v-for="item in helpItems"
        :key="item.title"
        :href="item.href"
        :target="item.target"
        :to="item.to"
        link
        class="child pl-8"
        @click="item.command && item.command()"
      >
        <v-list-item-icon>
          <v-badge
            v-if="item.badge"
            :value="item.badge.value"
            :content="item.badge.content"
            overlap
            right
            bordered
          >
            <v-icon>{{ item.icon }}</v-icon>
          </v-badge>

          <v-icon v-else>
            {{ item.icon }}
          </v-icon>
        </v-list-item-icon>

        <v-list-item-content>
          <v-list-item-title>{{ item.title }}</v-list-item-title>
        </v-list-item-content>
      </v-list-item>
    </v-list-group>

    <v-list-item class="main-menu__logout-btn" @click="logout">
      <v-list-item-icon>
        <v-icon>$sl-logout</v-icon>
      </v-list-item-icon>
      <v-list-item-title>Logga ut</v-list-item-title>
    </v-list-item>

    <p v-if="appVersion" class="caption text-center mb-0 mr-2" v-html="appVersion" />
  </v-list>
</template>

<script>
import {mapActions, mapGetters} from 'vuex'
import API from '@/services/api'
import { bus } from '@/utils/bus'
import Downloads from '@/components/mixins/Downloads.js'
// @group Common
// MainMenu component
export default {
  name: 'MainMenu',

  mixins: [Downloads],

  data: () => ({
    items: [
      {
        title: 'Personer',
        icon: '$sl-multiple-neutral',
        to: {name: 'PersonList'},
        enforce: ['apprentice', 'list'],
        class: 'v-step-2'
      },
      {
        title: 'Företag',
        icon: '$sl-bulding-day-light',
        to: {name: 'Companies'},
        enforce: ['company', 'list'],
        class: 'v-step-3'
      },
      {
        title: 'Anmälningar',
        icon: '$sl-business-contract-approve',
        to: '/applications',
        enforce: ['application', 'list'],
        class: 'v-step-4'
      },
      {
        title: 'Beställningar',
        icon: '$sl-notes-paper-approve',
        to: {name: 'Orders'},
        enforce: ['order', 'list'],
        class: 'v-step-5'
      },
      {
        title: 'Statistik',
        icon: '$sl-statistics',
        to: '/statistics',
        enforce: ['application', 'list'],
        class: 'v-step-6'
      },
    ],
    scbDialog: false,
    scbYear: '',
    adminItems: [
      {title: 'Användare', icon: '$sl-staff', to: '/users', enforce: ['user', 'list'], class: 'v-step-7'},
      {
        title: 'Anställningstyper',
        icon: '$sl-multiple-actions',
        to: {name: 'AdminTypes', params: {item: 'employmenttype'}}
      },
      {title: 'Avtalstyper', icon: '$sl-business-contract', to: {name: 'AdminTypes', params: {item: 'contracttype'}}},
      {
        title: 'Yrken',
        icon: '$sl-profession-man-construction',
        to: {name: 'AdminTypes', params: {item: 'professions'}}
      },
      {title: 'Yrkeskurser', icon: '$sl-school-teacher', to: {name: 'ProfessionCourses'}},
      {title: 'Ytterligare yrke/maskinslag', icon: '$sl-widening', to: {name: 'ProfessionWidening'}},
      {title: 'Distansutbildningar', icon: '$sl-elearning-book-smartphone', to: {name: 'DistanceEducations'}},
      {title: 'E-boksmoment', icon: '$sl-toys-lego', to: {name: 'BookElements'}},
      {title: 'Inställningar', icon: '$sl-cog-square', to: {name: 'SystemSettings'}},
      {title: 'Handledarinstruktioner', icon: '$sl-book-flip-page', to: {name: 'InstructorInstructions'}},
      {title: 'Misslyckade distansbeställningar', icon: '$sl-notes-paper-approve', canDisplay: (currentUser) => currentUser.userrole.isAdmin, to: {name: 'FailedEducations'}},
      {title: 'SCB Statistik', icon: '$sl-statistics', onClick: 'openScbDialog', tag: 'a'}
    ],
    releaseNotes: []
  }),

  created() {
    this.listReleaseNotes()
    bus.$on('release-published', this.listReleaseNotes);
  },

  beforeDestroy() {
    bus.$off('release-published', this.listReleaseNotes);
  },

  computed: {
    ...mapGetters('currentuser', ['currentPermissions']),
    ...mapGetters('currentuser', ['currentUser']),
    ...mapGetters('app', ['apiVersion']),

    hasUnseenReleaseNotes() {
      return this.releaseNotes.some(note => {
        const lastSeenId =
          note.application === 'registret'
            ? this.currentUser.lastSeenRegistretReleaseNoteId
            : this.currentUser.lastSeenMinasidorReleaseNoteId

        return lastSeenId == null || note.id > lastSeenId && note.status === 'published'
      })
    },

    helpItems() {
      return [
        {
          title: 'BYN:s anvisningar',
          icon: '$sl-book-flip-page',
          href: 'https://byns.sharepoint.com/SitePages/Hitta-anvisningar.aspx',
          target: '_blank'
        },
        {
          title: 'Guide',
          icon: '$sl-outdoors-landscape',
          command: this.openGuide
        },
        {
          title: 'Versionshistorik',
          icon: 'mdi-history',
          to: { name: 'ReleaseNotes' },
          badge: {
            value: this.hasUnseenReleaseNotes,
            content: '!'
          }
        },
        {
          title: 'Hjälp',
          icon: '$sl-question-circle',
          command: this.clickHelp
        },
        {
          title: 'Support',
          icon: '$sl-send',
          href: 'mailto:support@byn.se',
          target: '_blank'
        },
        {
          title: 'Manual: Registret',
          icon: '$sl-manual',
          href: 'https://byns.sharepoint.com/SitePages/GUIDE%20Manual-Registret.aspx',
          target: '_blank'
        },
        {
          title: 'Manual: Mina sidor',
          icon: '$sl-manual',
          href: 'https://www.byggbranschensyrkesnamnd.se/wp-content/uploads/2026/07/Manual-Mina-Sidor.docx',
          target: '_blank'
        },
        {
          title: 'Frånvaroberäknare kvalificeringstid',
          icon: '$sl-task-list-approve',
          href: 'https://www.byggbranschensyrkesnamnd.se/wp-content/uploads/2024/05/Franvaroberakning_kvalificeringstid.xlsx',
          target: '_blank'
        }
      ]
    },

    appVersion() {
      return `Version ${process.env.VUE_APP_VERSION} &middot; API ${this.apiVersion}`
    }
  },

  watch: {
    currentPermissions: {
      handler: (newVal, OldVal) => {},
      deep: true,
      immediate: true
    }
  },

  methods: {
    ...mapActions('currentuser', ['setCurrentUser', 'setNotifications']),
    ...mapActions('user', ['setInitiatorUser', 'clearInitiatorUser']),
    /**
     * @vuese
     * Used to open site tour.
     */
    openGuide() {
      if (this.$vuetify.breakpoint.mdAndUp == true) {
        this.$events.fire('openLeftMenu')
      } else {
        this.$events.fire('closeLeftMenu')
        console.log('closeLeftMenu')
      }
      if (this.$route.path != '/') {
        this.$router.push({name: 'Start', params: {guide: true}})
      } else {
        this.$router.push({name: 'LoginReturn', params: {guide: true}})
      }
    },
    clickHelp() {
      window.open('https://byns.sharepoint.com/SitePages/Register4.aspx', '_blank')
    },
    handleAdminClick(admin) {
      this[admin.onClick]()
    },
    openScbDialog() {
      this.scbYear = ''
      this.scbDialog = true
    },
    async submitScbStatistics() {
      if (String(this.scbYear).length !== 4) return
      try {
        const resp = await API.postScbStatistics({year: parseInt(this.scbYear)})
        const blob = window.URL.createObjectURL(new Blob([resp.data], {type: resp.data.type}))
        this.downloadFile(`scb-statistik-${this.scbYear}.xlsx`, blob)
        this.scbDialog = false
      } catch (e) {
        this.$events.fire('showError', 'Kunde inte hämta SCB statistik')
      }
    },
    async logout() {
      const res = await this.$dialog.confirm({
        title: 'Är du säker på att du vill logga ut?',
        text: '',
        actions: [
          {text: 'Avbryt', key: false, icon: '$sl-cancel', outlined: true },
          {
            text: 'Logga ut',
            key: true,
            icon: '$sl-logout',
            outlined: false
          }
        ]
      })

      if (res == true) {
        this.setCurrentUser(null)
        this.clearInitiatorUser()

        this.$events.fire('loggedOut')
        if (this.$route.path != '/') {
          this.$router.push('/').catch(() => {})
        }
      } else {
        return
      }
    },

    async listReleaseNotes() {
      try {
        const res = await API.listReleaseNotes(['registret','mina-sidor'], 'published')
        this.releaseNotes = res.data || []
      } catch (error) {
        console.log(error);
      }
    },
  }
}
</script>

<style lang="scss" scoped>
  .title.mb-0 {
    max-width: 190px;
    text-overflow: ellipsis;
    overflow: hidden;
  }

  .main-menu {
    height: 100vh;
    display: flex;
    flex-direction: column;

    &> * {
      flex: 0 0 auto;
    }

    &__logout-btn {
      margin-top: auto;
    }
  }
</style>
